import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { adsEnabled } from '../../utils/adsEnabled';

const Panel = styled(motion.div)`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 50;
  width: 20rem;
  max-width: calc(100vw - 2rem);
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCardHover};
  overflow: hidden;
`;

const ImgWrap = styled.div`
  position: relative;
`;

const Img = styled.img`
  width: 100%;
  height: 8rem;
  object-fit: cover;
  display: block;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  &:hover {
    background: rgba(15, 23, 42, 0.75);
  }
`;

const SponsoredTag = styled.span`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  padding: 0.125rem 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.maroon};
  color: ${({ theme }) => theme.colors.maroonForeground};
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Body = styled.div`
  padding: 1rem;
`;

const Title = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Desc = styled.p`
  margin: 0.25rem 0 0 0;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.75rem;
  gap: 0.5rem;
`;

const CtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  text-decoration: none;
  &:hover {
    opacity: 0.92;
  }
`;

const NoThanks = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

function AdPopup({ ad, delay = 8000 }) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!adsEnabled()) return undefined;
    const t = setTimeout(() => {
      const key = `ad-popup-${ad.id}-dismissed`;
      if (!sessionStorage.getItem(key)) setVisible(true);
    }, delay);
    return () => clearTimeout(t);
  }, [ad.id, delay]);

  const dismiss = () => {
    sessionStorage.setItem(`ad-popup-${ad.id}-dismissed`, 'true');
    setDismissed(true);
    setVisible(false);
  };

  if (!adsEnabled() || dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <Panel
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          <ImgWrap>
            <Img src={ad.image} alt={ad.title} loading="lazy" />
            <SponsoredTag>Sponsored</SponsoredTag>
            <CloseBtn type="button" onClick={dismiss} aria-label="Close ad">
              <X size={14} />
            </CloseBtn>
          </ImgWrap>
          <Body>
            <Title>{ad.title}</Title>
            <Desc>{ad.description}</Desc>
            <Actions>
              <CtaLink href={ad.url} target="_blank" rel="noopener noreferrer sponsored">
                {ad.cta} <ExternalLink size={12} />
              </CtaLink>
              <NoThanks type="button" onClick={dismiss}>
                No thanks
              </NoThanks>
            </Actions>
          </Body>
        </Panel>
      )}
    </AnimatePresence>
  );
}

export default AdPopup;
